<template>
  <div :class="['additional-info', { top: isTop }]">
    <button
      v-if="iconAudio && (isReceivedAudio || isDownloading) && isEnabledAudioPlay"
      class="button-icon button-icon-small button-rounded button-dim"
      @click="playAudio"
    >
      <component :is="iconAudio" class="icon-small icon-dimmer" />
      <SoundWaveIcon v-if="isPlayingAudio" />
    </button>

    <button class="button-icon button-icon-small button-rounded button-dim" @click="copyText">
      <CopyIcon class="icon-small icon-dimmer" />
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { storeToRefs } from 'pinia';

import CopyIcon from '@/components/icons/IconCopy.vue';
import SoundIcon from '@/components/icons/IconSound.vue';
import IconSoundLoading from '@/components/icons/IconSoundLoading.vue';
import SoundWaveIcon from '@/components/icons/IconSoundWave.vue';
import SpinnerIcon from '@/components/icons/IconSpinner.vue';
import { useAudioStore } from '@/stores/audio';
import { useToastStore } from '@/stores/toast';
import { ChatMessage } from '@/types/socket';

const { t } = useI18n();

const toastStore = useToastStore();
const audioStore = useAudioStore();

const { isDownloading, isTop, isLastIsOther, isTarget, message } = defineProps<{
  isDownloading: boolean;
  isPlayingAudio: boolean;
  isTop: boolean;
  isLastIsOther: boolean;
  isTarget: boolean;
  message: ChatMessage;
  playAudio: () => void;
}>();

const { languageOptions } = storeToRefs(audioStore);
const autoCompleteSecondsLeft = ref(0);
let autoCompleteInterval = 0;

const isEnabledAudioPlay = computed<boolean>(() => {
  const languageArray = isTarget ? [...message.tgtLanguageSet] : [...message.srcLanguageSet];

  return languageArray.every((lang) => languageOptions.value[lang].voiceOutputEnabled);
});

// 오디오 수신 여부
const isReceivedAudio = computed(() => {
  if (!isLastIsOther) return true;
  if (autoCompleteSecondsLeft.value < 1) return true;
  if (!isEnabledAudioPlay.value) return false;

  const flag = isTarget ? 'tgt' : 'src';
  const isAudioComplete = message[flag].isAudioComplete === true;
  const isReset = message.reset === '1';

  return isAudioComplete || isReset;
});

// 수동재생 버튼 아이콘
const iconAudio = computed(() => {
  if (!isEnabledAudioPlay.value) return null;

  return isDownloading ? SpinnerIcon : isReceivedAudio.value ? SoundIcon : IconSoundLoading;
});

// 5초 뒤 무조건 로딩 종료
const resetAutoComplete = () => {
  autoCompleteSecondsLeft.value = 5;
  if (autoCompleteInterval) clearInterval(autoCompleteInterval);
  autoCompleteInterval = setInterval(() => {
    autoCompleteSecondsLeft.value--;
    if (autoCompleteSecondsLeft.value === 0) clearInterval(autoCompleteInterval);
  }, 1000);
};

// 메세지 복사
const copyText = () => {
  const copyTarget = isTarget ? 'tgt' : 'src';
  const text = message[copyTarget].contents.map((item) => item.messageEdited || item.content).join('');
  navigator.clipboard.writeText(text);
  toastStore.info(t('copied'));
};

// 메세지 변경 후 5초 내 로딩 종료
watch(
  () => (isTarget ? message.tgt.contents : message.src.contents),
  () => resetAutoComplete()
);
</script>

<style scoped lang="scss">
.additional-info {
  position: absolute;
  display: flex;
  justify-content: flex-end;
  height: fit-content;
  bottom: 1.25rem;
  gap: 0.25rem;
}

.top {
  position: absolute;
  top: 0.25rem;
}
</style>
